/**
 * Returns the first argument if it is not null or undefined,
 * otherwise returns the second argument.
 *
 * @template L - The type of the first argument.
 * @template R - The type of the second argument.
 * @param primary - The first value to check.
 * @param fallback - The default value if the first is null or undefined.
 * @returns The first value if defined, otherwise the second.
 */
export function either<T, R>(primary: T, fallback: R) {
  return primary ?? fallback
}

/**
 * Returns the provided value if it is not null or undefined,
 * otherwise returns 0.
 *
 * @template L - The type of the provided value.
 * @param value - The value to check.
 * @returns The provided value if defined, otherwise 0.
 */
export function eitherOrZero<T>(value: T) {
  return either<T, 0>(value, 0)
}

/**
 * Returns the provided value if it is not null or undefined,
 * otherwise returns an empty array.
 *
 * @template L - The type of the provided value.
 * @param value - The value to check.
 * @returns The provided value if defined, otherwise empty array.
 */
export function eitherOrArray<T>(value: T) {
  return either<T, []>(value, [])
}

/**
 * Returns the provided value if it is not null or undefined,
 * otherwise returns an empty string.
 *
 * @template L - The type of the provided value.
 * @param value - The value to check.
 * @returns The provided value if defined, otherwise empty string.
 */
export function eitherOrString<T>(value: T) {
  return either<T, ''>(value, '')
}

/**
 * Returns the provided value if it is not null or undefined,
 * otherwise returns an translator key.
 *
 * @template L - The type of the provided value.
 * @param value - The value to check.
 * @returns The provided value if defined, otherwise empty string.
 */
export function unknownEither<T, R>(primary: T, fallback: R) {
  if (primary && primary !== 'unknown') {
    return primary
  }

  return fallback
}
